import './Transactions.css';
import SectionCard from '@frontend/Components/SectionCard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown, faArrowUp, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import { useQuery } from '@tanstack/react-query';
import FinancialService from '../../../AppSettings/services/FinancialService';
import VenExpService from '../../../AppSettings/services/VenExpService';
import OppExpService from '../../../AppSettings/services/OppExpService';
import GenExpensesService from '../../../AppSettings/services/GenExpensesService';
import FixBillService from '../../../AppSettings/services/FixBillService';
import InvoiceService from '../../../AppSettings/services/InvoiceService';
import { buildInvoiceNumberMap, resolveInvoiceNumber } from '../../../helpers/invoiceNumber';
import { normalizeToArray } from '../../../helpers/responseHelpers';
import { parseLocalDate } from '../../../helpers/dateHelpers';
import { computeBilledTimeAmount } from '../../../helpers/billingHelpers';

interface Transaction {
  id: string;
  type: string;
  displayType: 'Payment Received' | 'Expense Recorded' | 'Invoice Generated';
  amount: number;
  description: string;
  date: Date;
  employee: string;
  category: 'income' | 'expense' | 'invoice';
}

function formatPostingDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const Transactions: React.FC = () => {
  const { data: transactions = [], isPending: isLoading, error } = useQuery<Transaction[]>({
    queryKey: ['dashboard-recent-transactions'],
    queryFn: async () => {
      const [
        billedTimeData, fixedFeesData, otherBillingData,
        vendorData, operatingData, generalData, fixedBillsData,
        invoicesData
      ] = await Promise.all([
        FinancialService.getAllBilledTime(),
        FinancialService.getAllAssignedFinancialData(),
        FinancialService.getAllOtherBilling(),
        VenExpService.getAllVenExpenses(),
        OppExpService.getAllOppExpenses(),
        GenExpensesService.getAllGenExpenses(),
        FixBillService.getAllFixBillExpenses(),
        InvoiceService.getAllInvoices()
      ]);

      const formatted: Transaction[] = [];

      const billedEntries = normalizeToArray(billedTimeData);
      const fixedFeesList = normalizeToArray(fixedFeesData as any);
      const otherBillingList = normalizeToArray(otherBillingData);
      const vendorList = normalizeToArray(vendorData);
      const operatingList = normalizeToArray(operatingData);
      const generalList = normalizeToArray(generalData);
      const fixedBillsList = normalizeToArray(fixedBillsData);
      const invoicesList = normalizeToArray(invoicesData);
      const invoiceNumberMap = buildInvoiceNumberMap(invoicesList);

      billedEntries.forEach((item: any, index: number) => {
        formatted.push({
          id: `BT-${item.bt_id ?? item.id ?? index}`,
          type: "Billable Time",
          displayType: "Payment Received",
          amount: computeBilledTimeAmount(item),
          description: `$${item.billedrate}/hr - ${item.time_recorded}`,
          date: (item.date_recorded ?? item.date) ? parseLocalDate(item.date_recorded ?? item.date) : new Date(),
          employee: item.employee_name || "Unknown",
          category: "income"
        });
      });

      fixedFeesList.forEach((matter: any, mIdx: number) => {
        (matter.fixed_fees || []).forEach((fee: any, index: number) => {
          formatted.push({
            id: `FF-${matter.matterid ?? matter.matter_id ?? mIdx}-${fee.assignedid ?? fee.fee_code ?? index}`,
            type: "Fixed Fees",
            displayType: "Payment Received",
            amount: Number.isFinite(parseFloat(fee.fees)) ? parseFloat(fee.fees) : 0,
            description: `${fee.fee_code || ''} - ${fee.narrative || ''} (${matter.matter_name || ''})`,
            date: fee.date_added ? parseLocalDate(fee.date_added) : new Date(),
            employee: "N/A",
            category: "income"
          });
        });
      });

      otherBillingList.forEach((item: any, index: number) => {
        formatted.push({
          id: `OB-${item.ob_id ?? item.id ?? index}`,
          type: "Other Billing",
          displayType: "Payment Received",
          amount: Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0,
          description: item.description || item.narrative || "",
          date: (item.date ?? item.date_added) ? parseLocalDate(item.date ?? item.date_added) : new Date(),
          employee: item.employee_name || "N/A",
          category: "income"
        });
      });

      vendorList.forEach((item: any) => {
        formatted.push({
          id: `VE-${item.ven_id}`,
          type: "Vendor Services",
          displayType: "Expense Recorded",
          amount: Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0,
          description: item.description,
          date: parseLocalDate(item.date_added),
          employee: "Unknown Agent",
          category: "expense"
        });
      });

      operatingList.forEach((item: any) => {
        formatted.push({
          id: `OE-${item.opp_id}`,
          type: "Operating",
          displayType: "Expense Recorded",
          amount: Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0,
          description: item.description,
          date: parseLocalDate(item.date_added),
          employee: "Unknown Agent",
          category: "expense"
        });
      });

      generalList.forEach((item: any) => {
        formatted.push({
          id: `GE-${item.gen_id}`,
          type: "General",
          displayType: "Expense Recorded",
          amount: Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0,
          description: item.description,
          date: parseLocalDate(item.date_added),
          employee: "Unknown Agent",
          category: "expense"
        });
      });

      fixedBillsList.forEach((item: any) => {
        formatted.push({
          id: `FB-${item.bill_id}`,
          type: "Fixed Bills",
          displayType: "Expense Recorded",
          amount: Number.isFinite(parseFloat(item.amount)) ? parseFloat(item.amount) : 0,
          description: item.description,
          date: parseLocalDate(item.date_added),
          employee: "Unknown Agent",
          category: "expense"
        });
      });

      invoicesList.forEach((item: any) => {
        const totalAmount = Number.isFinite(parseFloat(item.total_amount)) ? parseFloat(item.total_amount) : 0;
        if (totalAmount <= 0) return;
        // `amount_paid`/`paid` reflect real payments (acc_rec_total is the AR subtotal, not payments).
        const paid = parseFloat(item.amount_paid || 0) || 0;
        const isPaid = item.paid === true || paid >= totalAmount;
        if (!isPaid) {
          formatted.push({
            id: `IN-${item.invoiceid}`,
            type: "Invoice",
            displayType: "Invoice Generated",
            amount: totalAmount,
            description: `Invoice #${resolveInvoiceNumber(item.invoiceid, invoiceNumberMap)}`,
            // Invoices expose `created_at` (not date_added); falling back to now misorders them.
            date: item.created_at ? parseLocalDate(item.created_at) : new Date(),
            employee: "N/A",
            category: "invoice"
          });
        } else if (item.payment_completed_time) {
          formatted.push({
            id: `IN-PAY-${item.invoiceid}`,
            type: "Invoice Payment",
            displayType: "Payment Received",
            amount: totalAmount,
            description: `Invoice #${resolveInvoiceNumber(item.invoiceid, invoiceNumberMap)}`,
            date: new Date(item.payment_completed_time),
            employee: "N/A",
            category: "income"
          });
        }
      });

      return formatted.sort((a, b) => b.date.getTime() - a.date.getTime()).slice(0, 5);
    },
    staleTime: 2 * 60 * 1000,
  });

  const getTransactionIcon = (category: string) => {
    switch (category) {
      case 'income': return faArrowDown;
      case 'expense': return faArrowUp;
      default: return faFileAlt;
    }
  };

  const getIconClass = (category: string) => {
    switch (category) {
      case 'income': return 'general-transaction-icon--income';
      case 'expense': return 'general-transaction-icon--expense';
      default: return 'general-transaction-icon--invoice';
    }
  };

  const formatAmount = (amount: number, category: string) => {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD',
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(amount);
    if (category === 'income') return `+${formatted}`;
    if (category === 'expense') return `-${formatted}`;
    return formatted;
  };

  return (
    <SectionCard
      title="Recent Transactions"
      right={<a href="/transactions" className="general-view-all-link">View All</a>}
      className="general-recent-transactions"
    >
      {isLoading ? (
        <p>Loading transactions...</p>
      ) : error ? (
        <p className="general-error-message">Failed to fetch transactions data.</p>
      ) : (
        <div className="general-transactions-list">
          {transactions.map((transaction, index) => {
            const isLast = index === transactions.length - 1;
            return (
              <div
                key={transaction.id}
                className={`general-transaction-item ${transaction.category} ${isLast ? 'general-transaction-item--last' : ''}`}
              >
                <div className={`general-transaction-icon-container ${getIconClass(transaction.category)}-container`}>
                  <FontAwesomeIcon
                    icon={getTransactionIcon(transaction.category)}
                    className={`general-transaction-icon ${getIconClass(transaction.category)}`}
                  />
                </div>
                <div className="general-transaction-details">
                  <div className="general-transaction-type">{transaction.type}</div>
                  <div className="general-transaction-description">{transaction.description}</div>
                </div>
                <div className="general-transaction-amount-container">
                  <div className={`general-transaction-amount ${transaction.category}`}>
                    {formatAmount(transaction.amount, transaction.category)}
                  </div>
                  <div className="general-transaction-time">{formatPostingDate(transaction.date)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
};

export default Transactions;
